import type { TPlayerBase } from 'video.js'
import type { TEnable } from './index.h'

import videojs from 'video.js'
import { isChromecastAPIAvailable } from './lib'
import * as script from './script'
import { createSessionManager } from './sessionManager'
import { ChromecastTech } from './tech'

// reexport helpers
export * from './lib'

/**
 * Configures the Context
 * casting context: https://developers.google.com/cast/docs/reference/chrome/cast.framework.CastContext
 * which is required before casting.
 */
function configureCastContext(options) {
  const castContext = cast.framework.CastContext.getInstance()

  castContext.setOptions({
    receiverApplicationId:
      options.receiverAppID || chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    // Allow this sender to automatically rejoin an existing Chromecast session
    // for the same tab/origin (e.g. after a page reload).
    autoJoinPolicy: chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED,
    androidReceiverCompatible: true,
    // Try to resume a previously saved session if one exists.
    resumeSavedSession: true,
  })
}

function onChromecastRequested(player) {
  player.chromecastSessionManager.openCastMenu()
}

/**
 * Adds the Chromecast button listener for the `chromecastRequested` event.
 */
function setUpChromecastButton(player) {
  player.on('chromecastRequested', onChromecastRequested.bind(null, player))
}

/**
 * Even cast api has '__onGCastApiAvailable' event, it does not indicate cast api is ready to be used.
 */
const CHECK_AVAILABILITY_INTERVAL = 500
const CHECK_AVAILABILITY_TIMEOUT = 7 * 1000
function waitUntilChromecastAPIsAreAvailable(player, options) {
  const maxTries = CHECK_AVAILABILITY_TIMEOUT / CHECK_AVAILABILITY_INTERVAL
  let tries = 1
  let intervalID: number | undefined

  player.on('dispose', () => clearInterval(intervalID))

  intervalID = setInterval(function () {
    if (tries > maxTries) return clearInterval(intervalID)

    if (isChromecastAPIAvailable()) {
      clearInterval(intervalID)
      enable(player, options)
    }

    tries = tries + 1
  }, CHECK_AVAILABILITY_INTERVAL) as unknown as number
}

/**
 * Start plugin if cast API available and tech registered
 */
const enable: TEnable = (player, options) => {
  if (isChromecastAPIAvailable()) {
    configureCastContext(options)
    createSessionManager(player)
    setUpChromecastButton(player)
  } else {
    waitUntilChromecastAPIsAreAvailable(player, options)
  }
}

/**
 * Registers the Chromecast plugin with Video.js.
 * Adds a 'chromecast' function to Video.js player instances.
 *
 * Example usage:
 * ```typescript
 * // Initialize player and enable chromecast
 * const player = videojs('myVideoElement', playerOptions);
 * player.chromecast({ receiverAppID: '1234'})
 * ```
 */
export function getReady() {
  videojs.registerPlugin('chromecast', function (options) {
    // Called when player.chromecast() is invoked
    // Requires player instance and cast framework to be available

    // setup tech
    videojs.registerTech('chromecast', ChromecastTech)

    // @ts-ignore
    const player: TPlayerBase = this
    enable(player, options ?? {})
  })

  // load cast scripts
  return script.fetch()
}
